/**
 * Evidence Links — resolve prose citations into fact-tool rows (D28).
 *
 * Citation shape in markdown: [label](evidence:<id>)
 * Id shape mirrors replays_service/domain/facts: kind:player:start or battle:start.
 * A citation that does not resolve is never rendered as a seekable time.
 */

export const EVIDENCE_SCHEME = 'evidence:';

const KNOWN_KINDS = new Set([
  'worker_gap',
  'supply_block',
  'battle',
  'loss_bucket',
  'sighting',
  'production',
  'research',
]);

/** @param {string} id */
export function evidenceHref(id) {
  return `${EVIDENCE_SCHEME}${id}`;
}

/**
 * @param {string|undefined} href
 * @returns {string|null} id or null when not an Evidence Link
 */
export function parseEvidenceHref(href) {
  if (typeof href !== 'string' || !href.startsWith(EVIDENCE_SCHEME)) {
    return null;
  }
  const id = href.slice(EVIDENCE_SCHEME.length);
  return id || null;
}

/**
 * @param {string} id
 * @returns {{ kind: string, player: string, start: number }|null}
 */
export function parseEvidenceId(id) {
  if (typeof id !== 'string' || !id) return null;
  const parts = id.split(':');
  if (parts.length < 2) return null;
  const kind = parts[0];
  if (!KNOWN_KINDS.has(kind)) return null;
  const start = Number(parts[parts.length - 1]);
  if (!Number.isFinite(start)) return null;
  if (kind === 'battle') {
    if (parts.length !== 2) return null;
    return { kind, player: '', start };
  }
  if (kind === 'production' || kind === 'research') {
    // production:player:name:unitID:start — read from the right.
    if (parts.length < 5) return null;
    if (!Number.isFinite(Number(parts[parts.length - 2]))) return null;
    const player = parts.slice(1, -3).join(':');
    if (!player) return null;
    return { kind, player, start };
  }
  if (kind === 'sighting') {
    // Current: sighting:observer:unitID:start; legacy: sighting:observer:start
    if (parts.length >= 4 && Number.isFinite(Number(parts[parts.length - 2]))) {
      const player = parts.slice(1, -2).join(':');
      return { kind, player, start };
    }
    if (parts.length < 3) return null;
    return { kind, player: parts.slice(1, -1).join(':'), start };
  }
  if (parts.length < 3) return null;
  const player = parts.slice(1, -1).join(':');
  return { kind, player, start };
}

/**
 * Build a lookup of ids → { id, kind, player, start, end } from tool payloads.
 * @param {{
 *   macroResults?: Array<{ player?: string, issues?: Array<object> }>,
 *   battles?: Array<object>,
 *   losses?: { lanes?: Array<{ player?: string, buckets?: Array<object> }> },
 *   sightings?: Array<object>,
 *   arrivals?: Array<object>,
 *   axes?: Array<object>,
 * }} sources
 */
export function buildEvidenceIndex(sources = {}) {
  /** @type {Map<string, { id: string, kind: string, player: string, start: number, end: number }>} */
  const index = new Map();

  const add = (id, kind, player, start, end) => {
    if (!id || !Number.isFinite(start)) return;
    index.set(id, {
      id,
      kind: kind || '',
      player: player || '',
      start,
      end: Number.isFinite(end) ? end : start,
    });
  };

  for (const result of sources.macroResults || []) {
    const player = result?.player || '';
    for (const issue of result?.issues || []) {
      const id = issue.id;
      add(id, issue.type, player, issue.start_real_time, issue.end_real_time);
    }
  }

  for (const battle of sources.battles || []) {
    add(battle.id, 'battle', '', battle.start, battle.end);
  }

  for (const lane of sources.losses?.lanes || []) {
    const player = lane?.player || '';
    for (const bucket of lane?.buckets || []) {
      add(bucket.id, 'loss_bucket', player, bucket.start, bucket.end);
    }
  }

  for (const sighting of sources.sightings || []) {
    add(sighting.id, 'sighting', sighting.observer || '', sighting.real_time, sighting.real_time);
  }

  for (const arrival of sources.arrivals || []) {
    add(arrival.id, 'sighting', arrival.observer || '', arrival.real_time, arrival.real_time);
  }

  for (const digest of sources.axes || []) {
    const player = digest?.player || '';
    const opponent = digest?.opponent || '';
    for (const cite of [
      ...(digest?.army_strength?.cites || []),
      ...(digest?.economy?.cites || []),
      ...(digest?.aggression?.cites || []),
    ]) {
      add(cite.id, cite.kind, cite.player || player, cite.start, cite.end);
    }
    for (const row of digest?.scouting_reaction?.reactions || []) {
      add(row.arrival_id, 'sighting', player, row.at, row.at);
      for (const start of row.started || []) {
        const parsed = parseEvidenceId(start.id);
        if (parsed) add(start.id, parsed.kind, parsed.player, parsed.start, parsed.start);
      }
    }
    for (const row of digest?.army_adaptation?.research || []) {
      add(row.id, 'research', player, row.start, row.finish);
    }
    for (const row of digest?.army_adaptation?.opp_research || []) {
      add(row.id, 'research', opponent, row.start, row.finish);
    }
  }

  return index;
}

/**
 * @param {Map<string, object>|null|undefined} index
 * @param {string} id
 * @returns {object|null}
 */
export function resolveEvidence(index, id) {
  if (!index || !id) return null;
  return index.get(id) || null;
}
